import moment, { Moment } from 'moment-timezone';
import { Data } from 'react-csv/lib/core';
import { useHistory, useLocation } from 'react-router-dom';
import { z } from 'zod';

import { errorToast } from '../../toast';
import { TimeWindow, timeWindowSchema } from '../../utils/datetimes/types';
import { durationCalculationMethodSchema } from '../../utils/durationsFromServices';
import { removeUndefinedValues } from '../../utils/iteration';
import { useAxleMutation, useAxleQuery } from '../apiHelpers';
import { Address } from '../sharedSchemas/addresses';
import {
    nameAndCodeSchema,
    organizationBasicSchema,
    prioritySchema,
    qhpSimpleSchema,
    serviceAreaBasicSchema,
    territorySimple,
} from '../sharedSchemas/basicSchemas';
import { PaginationParameters } from '../sharedSchemas/pagination';
import { VisitChange, visitChangeSchema } from '../sharedSchemas/visitChangeLog';
import {
    VisitDetails,
    outcomeSchema,
    statusSchema,
    visitDetailsSchema,
    visitForListSchema,
} from '../sharedSchemas/visits';
import { serviceBasicSchema, serviceForListDisplaySchema } from './services';

export type JointAssignmentTrim = Record<string, string>;

export type VisitFilterParams = {
    startDate: Moment;
    endDate: Moment;
    visitFilters: Record<string, string[]>;
    searchText: string;
};

export type UseVisitOptions = VisitFilterParams &
    PaginationParameters & {
        suspense?: boolean;
    };

export const useVisits = ({
    startDate,
    endDate,
    page,
    pageSize,
    searchText,
    visitFilters,
    suspense = true,
}: UseVisitOptions) =>
    useAxleQuery({
        queryKey: ['visits', { startDate, endDate, page, pageSize, searchText, visitFilters }],
        path: '/visit',
        params: {
            start_datetime: moment(startDate).startOf('day').format(),
            end_datetime: moment(endDate).endOf('day').format(),
            page,
            page_size: pageSize,
            q: searchText,
            ...visitFilters,
        },
        suspense,
        keepPreviousData: true,
        responseSchema: z.object({
            visits: z.array(visitForListSchema),
            num_matching: z.number(),
            status_options: z.array(statusSchema),
        }),
        transform: (data) => ({
            visits: data.visits,
            numMatching: data.num_matching,
            statusOptions: data.status_options,
        }),
    }).data;

export const useVisitsWithPrefetching = (options: UseVisitOptions) => {
    useVisits({ ...options, page: options.page - 1, suspense: false });
    useVisits({ ...options, page: options.page + 1, suspense: false });
    return useVisits(options);
};

export const useVisit = ({ visitId }: { visitId: string }): VisitDetails => {
    const location = useLocation();
    const history = useHistory();
    return useAxleQuery({
        path: `/visit/${visitId}`,
        queryKey: ['visit', visitId],
        suspense: true,
        responseSchema: z.object({
            visit: visitDetailsSchema,
        }),
        onUnauthorized: () => {
            const isOnVisitDetailPage = (location: string) => /^\/visits\/[\w-]+/.test(location);
            if (isOnVisitDetailPage(location.pathname)) {
                history.push('/visits');
            }
            errorToast('You have lost access to this visit.');
            throw new Error('Unauthorized access to visit detail.');
        },
    }).data.visit;
};

export const useVisitChangelog = ({ visitId }: { visitId: string }): VisitChange[] | undefined => {
    return useAxleQuery({
        path: `/visit/${visitId}/changelog`,
        queryKey: ['visit-changelog', visitId],
        suspense: false,
        responseSchema: z.object({
            changelog: z.array(visitChangeSchema),
        }),
        transform: (data) => data.changelog,
    }).data;
};

export const useGeneralEditVisitOptions = () =>
    useAxleQuery({
        path: '/visit/edit/options',
        suspense: true,
        responseSchema: z.object({
            position_statuses: z.array(nameAndCodeSchema),
            position_statuses_with_outcomes: z.array(nameAndCodeSchema),
            default_position_status: nameAndCodeSchema,
            encounter_outcomes: z.array(nameAndCodeSchema),
            none_encounter_outcome: nameAndCodeSchema,
        }),
    }).data;

export const useSpecificEditVisitOptions = (visitId: string) =>
    useAxleQuery({
        path: `/visit/${visitId}/edit/options`,
        suspense: true,
        responseSchema: z.object({
            visit_statuses: z.array(nameAndCodeSchema),
            visit_statuses_with_outcomes: z.array(nameAndCodeSchema),
            visit_outcomes: z.array(nameAndCodeSchema),
            visit_priority_options: z.array(nameAndCodeSchema),
            scheduled_service_options: z.array(serviceForListDisplaySchema),
            preferred_timeslot_options: z.array(timeWindowSchema),
            duration_calculation_method: durationCalculationMethodSchema,
        }),
    }).data;

export const useEditVisit = ({
    visitId,
    patientId,
    orderId,
}: {
    visitId: string;
    patientId: string;
    orderId: string | null;
}) =>
    useAxleMutation({
        axiosConfig: ({
            status_code = undefined,
            outcome_code = undefined,
            start_time = undefined,
            requires_reschedule = undefined,
            tentative_date = undefined,
            earliest_fulfillment_time = undefined,
            latest_fulfillment_time = undefined,
            timeslot_preferences = undefined,
            priority_code = undefined,
            address = undefined,
            phone = undefined,
            email = undefined,
            clinician_notes = undefined,
            internal_notes = undefined,
            duration_override_minutes = undefined,
            positions_to_add,
            positions_to_edit,
            position_uuids_to_delete,
            should_send_patient_alerts,
        }: {
            status_code?: string;
            outcome_code?: string;
            start_time?: Moment;
            requires_reschedule?: boolean | null;
            tentative_date?: Date | null;
            earliest_fulfillment_time?: Moment | null;
            latest_fulfillment_time?: Moment | null;
            timeslot_preferences?: TimeWindow[];
            priority_code?: string | null;
            address?: Address;
            phone?: string;
            email?: string;
            clinician_notes?: string;
            duration_override_minutes?: number | null;
            internal_notes?: string;
            positions_to_add: {
                uuid: string;
                is_virtual: boolean;
                scheduled_service_uuids: string[] | undefined;
                completed_service_uuids: string[] | undefined;
                start_offset_minutes: number | null;
                duration_override_minutes: number | null;
                qhp_uuid: string;
                status_code: string;
                outcome_code: string | undefined;
                mileage_mi: number | null;
            }[];
            positions_to_edit: {
                uuid: string;
                is_virtual?: boolean;
                scheduled_service_uuids?: string[];
                completed_service_uuids?: string[];
                start_offset_minutes?: number | null;
                duration_override_minutes?: number | null;
                qhp_uuid?: string;
                status_code?: string;
                outcome_code?: string;
                outcome_notes?: string;
                mileage_mi?: number | null;
            }[];
            position_uuids_to_delete: string[];
            should_send_patient_alerts: boolean;
        }) => ({
            method: 'post',
            path: `/visit/${visitId}/edit`,
            data: removeUndefinedValues({
                status_code,
                outcome_code,
                requires_reschedule,
                start_time: start_time?.format(),
                // NOTE: We don't pass the following twp fulfillment-window fields
                //  through .format(), as we want to accept null values;
                tentative_date,
                earliest_fulfillment_time,
                latest_fulfillment_time,
                timeslot_preferences,
                priority_code,
                address,
                email,
                phone,
                duration_override_minutes,
                internal_notes,
                clinician_notes,
                positions_to_add,
                positions_to_edit: positions_to_edit.map(removeUndefinedValues),
                position_uuids_to_delete,
                should_send_patient_alerts,
            }),
        }),
        defaultSuccessMessage: 'Updated the visit!',
        // We need to hard reset `['visit', visitId]`, as it is very confusing
        //  to the user if the visit detail page doesn't update after they
        //  edit a visit.
        // Also, if we don’t do this, then since the edit dialog uses the
        //  `['visit', visitId]` query, it will show old data, and overwrite
        //  new visit data with old data. Very bad! Need to avoid at all costs.
        // Keep these synced with `useCancelVisit` and `useRescheduleVisit`.
        hardResetQueries: [['visit', visitId]],
        // We soft-reset these, as they take a long time to reload, and we don’t
        //  want to make the user wait so long for a hard reset. Updating in the
        //  background (with the little loading bar on the top to indicate progress)
        // is fine.
        softResetQueries: [
            ['availability', orderId],
            ['visits'],
            ['patient', patientId],
            ['schedule'],
            ['visit-requests'],
            ['service-orders', orderId],
            ['visit-changelog', visitId],
        ],
    });

const bulkEditResponseSchema = z.object({
    success_messages: z.array(z.string()),
    error_messages: z.array(z.string()),
});

export const useBulkEditVisits = ({
    visitIds,
    onSuccess,
}: {
    visitIds: string[];
    onSuccess: (data: z.infer<typeof bulkEditResponseSchema>) => void;
}) =>
    useAxleMutation({
        axiosConfig: ({
            set_visit_order_preferences,
        }: {
            set_visit_order_preferences?: boolean;
        }) => ({
            method: 'post',
            path: `/visit/bulk-edit`,
            data: removeUndefinedValues({
                visit_uuids: visitIds,
                set_visit_order_preferences,
            }),
            onSuccess,
        }),
        responseSchema: bulkEditResponseSchema,
        defaultSuccessMessage: '',
        // Keep these synced with `useEditVisit`.
        softResetQueries: [['visits'], ['schedule'], ['visit-requests']],
    });

export const useBulkUpdateVisitStateOutcome = ({
    visitIds,
    onSuccess,
}: {
    visitIds: string[];
    onSuccess: (data: z.infer<typeof bulkEditResponseSchema>) => void;
}) =>
    useAxleMutation({
        axiosConfig: ({
            status_code,
            outcome_code,
            should_send_patient_alerts,
        }: {
            status_code: string;
            outcome_code: string;
            should_send_patient_alerts: boolean;
        }) => ({
            method: 'post',
            path: `/visit/bulk-edit/visit-state-outcome`,
            data: removeUndefinedValues({
                visit_uuids: visitIds,
                status_code,
                outcome_code,
                should_send_patient_alerts,
            }),
            onSuccess,
        }),
        responseSchema: bulkEditResponseSchema,
        defaultSuccessMessage: '',
        // Keep these synced with `useEditVisit`.
        softResetQueries: [['visits'], ['schedule'], ['visit-requests']],
    });

export const useCancelVisit = ({ visitId, patientId }: { visitId: string; patientId: string }) =>
    useAxleMutation({
        axiosConfig: ({
            should_send_patient_alerts,
            cancellation_reason_uuid,
        }: {
            should_send_patient_alerts: boolean;
            cancellation_reason_uuid: string | null;
        }) => ({
            method: 'post',
            path: `/visit/${visitId}/cancel`,
            data: { should_send_patient_alerts, cancellation_reason_uuid },
        }),
        defaultSuccessMessage: 'Canceled the visit!',
        // Keep these synced with `useEditVisit`.
        hardResetQueries: [['visit', visitId]],
        softResetQueries: [
            ['visits'],
            ['patient', patientId],
            ['schedule'],
            ['visit-changelog', visitId],
            ['visit-requests'],
            ['service-orders'],
        ],
    });

export const useVisitFilterOptions = () =>
    // These are the available filter options when using the VisitFilterDialog.
    useAxleQuery({
        path: '/visit/filters',
        suspense: true,
        responseSchema: z.object({
            qhp: z.array(qhpSimpleSchema).nullable(),
            org: z.array(organizationBasicSchema).nullable(),
            territory: z.array(territorySimple).nullable(),
            service: z.array(serviceBasicSchema).nullable(),
            outcome: z.array(outcomeSchema).nullable(),
            status: z.array(statusSchema).nullable(),
            service_area: z.array(serviceAreaBasicSchema).nullable(),
            priority: z.array(prioritySchema),
        }),
    }).data;

export const useExportVisits = ({
    startDate,
    endDate,
    searchText,
    visitFilters,
}: VisitFilterParams): Data =>
    useAxleQuery({
        // We aren't caching the results of this query, so we don't need to include
        // visit data that may change independent of these filters
        queryKey: ['export', { startDate, endDate, searchText, visitFilters }],
        deleteCacheAfterUnmount: true,
        path: '/visit/export',
        params: {
            start_datetime: moment(startDate).startOf('day').format(),
            end_datetime: moment(endDate).endOf('day').format(),
            q: searchText,
            ...visitFilters,
        },
        responseSchema: z.object({
            // TODO: right now, when we use the below typing, we get a bunch of zod errors.
            //  We should fix this, but for now, we just use `z.any()` to avoid the errors.
            // csv_rows: z.array(z.array(z.string().or(z.array(z.any())).nullable())),
            csv_rows: z.array(z.array(z.any())),
        }),
        suspense: true,
    }).data.csv_rows;

export const useRescheduleVisit = ({
    visitId,
    patientId,
    orderId,
}: {
    visitId: string;
    patientId: string;
    orderId: string | null;
}) =>
    useAxleMutation({
        axiosConfig: (data: { time_option: TimeWindow; assignment: JointAssignmentTrim }) => ({
            method: 'post',
            path: `/visit/${visitId}/reschedule`,
            data: data,
        }),
        defaultSuccessMessage: 'Rescheduled the visit!',
        // We need to hard reset `['visit', visitId]`, as it is very confusing
        //  to the user if the visit detail page doesn't update after they
        //  edit a visit.
        // Also, if we don’t do this, then since the resch dialog uses the
        //  `['visit', visitId]` query, it will show old data, and overwrite
        //  new visit data with old data. Very bad! Need to avoid at all costs.
        // Keep these synced with `useCancelVisit` and `useEditVisit`.
        hardResetQueries: [['visit', visitId]],
        // We soft-reset these, as they take a long time to reload, and we don’t
        //  want to make the user wait so long for a hard reset. Updating in the
        //  background (with the little loading bar on the top to indicate progress)
        // is fine.
        softResetQueries: [
            ['visits'],
            ['patient', patientId],
            ['schedule'],
            ['visit-changelog', visitId],
            ['visit-requests'],
            ['service-orders', orderId],
        ],
    });

export const useBulkReassignVisits = () =>
    useAxleMutation({
        // { visit_id: { position_id: qhp_id } }
        axiosConfig: (positionAssignmentByVisitId: Record<string, Record<string, string>>) => ({
            method: 'post',
            path: `/visit/bulk-reassign`,
            data: {
                assignments_by_visit_id: positionAssignmentByVisitId,
            },
        }),
        defaultSuccessMessage: 'Reassigned the visits!',
        // Keep these synced with `useEditVisit`.
        hardResetQueries: [['visits'], ['schedule']],
    });
